import React, { useState, useEffect, useMemo, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Text, Group, Paper, Container, Title, Loader, Notification } from '@mantine/core';
import AutosaveIndicator from '../AutoSaveIndicator';
import {
  setActiveModelId,
  fetchScenarios,
  saveScenario,
  deleteScenario,
  fetchSecurityControls,
  saveSecurityControl,
  generateSecurityControls,
  persistScenarioAttribute,
  persistScenarioAttackSurface,
  persistScenarioImpact,
  persistScenarioAgents,
  selectScenarios,
  selectControls,
} from '../redux/slices/securitySlice';
import { useMergedSecurityForm } from '../redux/reduxutils/securityFormUtils';
import ScenarioBuilder from './ScenarioBuilder';
import EvaluationPanel from './EvaluationPanel';
import { fetchConfig } from '../redux/actions/configActions';
import { setAssessmentPosition } from '../redux/actions/riskActions';

export default function SecurityRiskAssessment({ modelId, assessmentUuid }) {
  const dispatch = useDispatch();
  const securityConfig = useMergedSecurityForm();
  // model-level assessmentData deprecated in scenario-only flow
  const modelType = useSelector((s) => s.risks.models[modelId]?.modelType || '');

  const [completedScenarios, setCompletedScenarios] = useState([]);
  const completedIdsRef = useRef(new Set());
  const [activeScenarioId, setActiveScenarioId] = useState(null);
  const [showEvaluation, setShowEvaluation] = useState(false);
  const [assessmentPhase, setAssessmentPhase] = useState('attributes');
  const [isLoading, setIsLoading] = useState(true);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'info' });
  const [isSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isGeneratingActions, setIsGeneratingActions] = useState(false);
  const securityControls = useSelector((s) => selectControls(s, assessmentUuid));


  const threatAgentsConfig = useMemo(
    () => securityConfig.find((c) => c.name === 'Threat Agents') || { schema: { attributes: {}, options: [] } },
    [securityConfig]
  );

  const vulnerabilityCategories = useMemo(() => {
    const isGenerative = (modelType || '').toLowerCase().includes('generative');
    return isGenerative ? ['Attack Surface - Generative AI'] : ['Attack Surface - Predictive AI'];
  }, [modelType]);

  // Attributes are now configured inside the EvaluationPanel; preview flow removed

  useEffect(() => {
    dispatch(setActiveModelId({ assessmentUuid, modelId }));
    dispatch(fetchConfig('securityImpactAssessment'));
    setIsLoading(false);
  }, [modelId, assessmentUuid, dispatch]);

  // Fetch scenarios only once per assessmentUuid
  const fetchedScenariosRef = useRef({});
  useEffect(() => {
    if (!assessmentUuid) return;
    if (fetchedScenariosRef.current[assessmentUuid]) return;
    fetchedScenariosRef.current[assessmentUuid] = true;
    dispatch(fetchScenarios({ modelId, assessmentUuid }));
  }, [dispatch, modelId, assessmentUuid]);
  // Derive scenarios directly from Redux; no local mirroring
  const persistedScenariosMap = useSelector((s) => selectScenarios(s, assessmentUuid));
  const scenarios = useMemo(() => {
    return Object.entries(persistedScenariosMap).map(([id, data]) => ({
      id,
      name: data.name || 'Untitled scenario',
      mappedAgents: Array.isArray(data.mappedAgents) ? data.mappedAgents : [],
      attributes: data.attributes || {},
      attackSurface: data.attackSurface || {},
      impact: data.impact || {},
      status: data.status || null,
      updatedAt: data.updatedAt,
    }));
  }, [persistedScenariosMap]);

  // Scenario keys derived from persisted map to avoid referencing 'scenarios' in effects
  const scenarioKeys = useMemo(() => Object.keys(persistedScenariosMap || {}), [persistedScenariosMap]);

  const activeScenario = useMemo(() => {
    if (!activeScenarioId) return null;
    const data = persistedScenariosMap[activeScenarioId];
    return data ? { id: activeScenarioId, ...data } : null;
  }, [activeScenarioId, persistedScenariosMap]);

  // Build assessment data directly from scenario attributes, attackSurface, and impact
  const assessmentData = useMemo(() => {
    if (!activeScenario?.id) return {};

    const scenario = persistedScenariosMap[activeScenario.id];
    if (!scenario) return {};

    const data = {};

    // Add scenario attributes
    if (scenario.attributes && Object.keys(scenario.attributes).length) {
      data['Scenario Attributes'] = scenario.attributes;
    }

    // Add attack surface (vulnerability category)
    const vulnCat = vulnerabilityCategories?.[0];
    if (vulnCat && scenario.attackSurface && Object.keys(scenario.attackSurface).length) {
      data[vulnCat] = scenario.attackSurface;
    }

    // Add impact assessment
    if (scenario.impact && Object.keys(scenario.impact).length) {
      data['Scenario Impact'] = scenario.impact;
    }

    return data;
  }, [activeScenario?.id, persistedScenariosMap, vulnerabilityCategories]);

  // Model-level autosave removed for scenario-only flow; persistence uses saveScenario snapshots

  const handleChange = (categoryName, subName, vals) => {
    if (!activeScenario?.id) return;
    if (categoryName === 'Scenario Attributes' && subName) {
      dispatch(persistScenarioAttribute({
        modelId,
        assessmentUuid,
        scenarioId: activeScenario.id,
        attributeKey: subName,
        attributeValue: vals,
      }));
      return;
    }
    if (categoryName === vulnerabilityCategories[0] && subName) {
      dispatch(persistScenarioAttackSurface({
        modelId,
        assessmentUuid,
        scenarioId: activeScenario.id,
        attackSurfaceUpdates: { [subName]: vals },
      }));
      return;
    }
    if (categoryName === 'Scenario Impact') {
      dispatch(persistScenarioImpact({
        modelId,
        assessmentUuid,
        scenarioId: activeScenario.id,
        impactUpdates: { [subName]: vals },
      }));
    }
  };

  const closeNotification = () => setNotification({ show: false, message: '', type: 'info' });

  const handleAddScenario = (scenario) => {
    const newScenario = { ...scenario, id: scenario.id || uuidv4() };
    dispatch(
      saveScenario({
        modelId,
        scenarioId: newScenario.id,
        name: newScenario.name || 'Untitled scenario',
        mappedAgents: [],
        attributes: {},
        assessmentUuid,
        status: 'active',
      })
    );
  };

  const handleRenameScenario = (scenarioId, name) => {
    dispatch(saveScenario({ modelId, scenarioId, name, assessmentUuid }));
  };

  const handleRemoveScenario = (scenarioId) => {
    setCompletedScenarios((prev) => prev.filter((s) => s.id !== scenarioId));
    if (activeScenarioId === scenarioId) {
      setActiveScenarioId(null);
      setShowEvaluation(false);
    }
    dispatch(deleteScenario({ modelId, assessmentUuid, scenarioId, cascade: true }));
  };

  const handleUpdateScenarioAgents = (scenarioId, agents) => {
    dispatch(persistScenarioAgents({
      modelId,
      assessmentUuid,
      scenarioId,
      mappedAgents: agents,
    }));
  };

  const handleStartEvaluation = (scenario) => {
    setActiveScenarioId(scenario?.id);
    setShowEvaluation(true);
    setAssessmentPhase('attributes');
  };

  const handleCompleteAssessment = (scenario, options = {}) => {
    const alreadyDone = completedIdsRef.current.has(scenario.id);
    if (!alreadyDone) {
      const completedScenario = {
        ...scenario,
        completedAttackSurface: assessmentData[vulnerabilityCategories[0]] || {},
        completedImpact: assessmentData['Scenario Impact'] || {},
        attributes: assessmentData['Scenario Attributes'] || scenario.attributes || {},
        controls: securityControls?.[scenario.id] || [],
      };
      setCompletedScenarios((prev) => [...prev, completedScenario]);
      completedIdsRef.current.add(scenario.id);
      // Persist status as completed
      dispatch(saveScenario({ modelId, scenarioId: scenario.id, status: 'completed' }));
    }
    if (options.showSummary) {
      setActiveScenarioId(scenario?.id);
      setShowEvaluation(true);
      setAssessmentPhase('summary');
      return;
    }
    if (options.close) {
      // Reset the right pane and return to add scenario flow
      setActiveScenarioId(null);
      setShowEvaluation(false);
      setAssessmentPhase('attributes');
    }
  };

  // Reflect scenario-based progress in the global AssessmentProgressIndicator
  const lastPositionRef = useRef(null);
  useEffect(() => {
    const scenarioCount = Array.isArray(scenarioKeys) ? scenarioKeys.length : 0;
    const foundIdx = activeScenarioId && scenarioCount > 0
      ? scenarioKeys.indexOf(activeScenarioId)
      : -1;
    const scenarioIndex = foundIdx >= 0 ? foundIdx + 1 : null;
    const securitySubStep = !showEvaluation || !activeScenarioId ? 'selectScenario' : assessmentPhase;

    const nextPosition = {
      majorStep: 'securityAssessment',
      scenarioId: activeScenarioId || null,
      scenarioName: activeScenario?.name || null,
      scenarioIndex,
      scenarioCount,
      securitySubStep,
    };

    const nextKey = JSON.stringify(nextPosition);
    if (lastPositionRef.current !== nextKey) {
      lastPositionRef.current = nextKey;
      dispatch(setAssessmentPosition(modelId, assessmentUuid, nextPosition));
    }
  }, [dispatch, modelId, assessmentUuid, showEvaluation, activeScenarioId, activeScenario?.name, assessmentPhase, scenarioKeys]);

  // Fetch scenario controls when entering Action Plan
  const fetchedOnceRef = useRef(new Set());
  useEffect(() => {
    if (assessmentPhase !== 'action-plan' || !activeScenario?.id) return;
    const key = `${assessmentUuid}:${activeScenario.id}`;
    const existing = securityControls[activeScenario.id];
    const alreadyFetched = fetchedOnceRef.current.has(key);
    if (!existing || existing.length === 0) {
      if (!alreadyFetched) {
        fetchedOnceRef.current.add(key);
        dispatch(fetchSecurityControls({ modelId, assessmentUuid, scenarioId: activeScenario.id }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentPhase, activeScenario?.id, assessmentUuid, modelId, securityControls]);

  const handleRefreshScenarioControls = async (scenario) => {
    if (!scenario?.id) return;
    // Reuse generation thunk by mapping scenario to risk-like shape
    try {
      setIsGeneratingActions(true);
      await dispatch(generateSecurityControls(modelId, assessmentUuid, [
        { risk_id: scenario.id, risk_name: scenario.name }
      ]));
    } finally {
      setIsGeneratingActions(false);
    }
    dispatch(fetchSecurityControls({ modelId, assessmentUuid, scenarioId: scenario.id }));
  };

  const handleSaveScenarioControl = async (control, scenarioId) => {
    try {
      await dispatch(saveSecurityControl({
        controlData: control,
        modelId,
        scenarioId,
        assessmentUuid
      }));
      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 1500);
    } catch (e) {
      console.error('Failed to save scenario control', e);
    }
  };

  return (
    <Container fluid px="xl" py="lg">
      {/* Header */}
      <Group justify="space-between" mb="lg">
        <Box>
          <Title order={3} c="dark.7" mb="xs">Threat & Vulnerability Assessment</Title>
          <Text size="sm" c="dimmed">Create threat scenarios and evaluate them side-by-side</Text>
        </Box>
        <Group gap="sm">
          {isLoading && <Loader size="sm" />}
          <AutosaveIndicator isSaving={isSaving} isSaved={isSaved} />
        </Group>
      </Group>

      {/* Layout */}
      <Box
        style={{
          display: 'grid',
          gridTemplateColumns: '5fr 7fr',
          gap: '24px',
          minHeight: 600,
        }}
      >
        <Paper withBorder radius="md" p="xl" sx={(t) => ({ backgroundColor: t.colors.gray[0], borderColor: t.colors.gray[2] })}>
          <ScenarioBuilder
            scenarios={scenarios}
            onAddScenario={handleAddScenario}
            onRemoveScenario={handleRemoveScenario}
            onStartEvaluation={handleStartEvaluation}
            completedScenarios={completedScenarios}
            securityConfig={securityConfig}
            securityControls={securityControls}
            onRenameScenario={handleRenameScenario}
          onResumeScenario={(scenario) => {
              const sid = scenario?.id;
              if (!sid) return;
              // If editing a completed scenario, mark it active again
              const currentStatus = persistedScenariosMap[sid]?.status || null;
              if ((scenario?.phase || 'attributes') === 'attributes' && currentStatus === 'completed') {
                dispatch(saveScenario({ modelId, scenarioId: sid, status: 'active' }));
              }
              setActiveScenarioId(sid);
              setShowEvaluation(true);
              setAssessmentPhase(scenario?.phase || 'attributes');
            }}
          />
        </Paper>

        <Paper withBorder radius="md" p="xl" sx={(t) => ({ backgroundColor: t.colors.gray[0], borderColor: t.colors.gray[2] })}>
          {showEvaluation && activeScenario ? (
            <EvaluationPanel
              scenario={activeScenario}
              assessmentData={assessmentData}
              vulnerabilityCategories={vulnerabilityCategories}
              handleChange={handleChange}
              assessmentPhase={assessmentPhase}
              setAssessmentPhase={setAssessmentPhase}
              threatAgentsConfig={threatAgentsConfig}
              securityConfig={securityConfig}
              onUpdateScenarioAgents={handleUpdateScenarioAgents}
              onCompleteAssessment={handleCompleteAssessment}
              // Action Plan props
              modelId={modelId}
              assessmentUuid={assessmentUuid}
              securityControls={securityControls}
              onRefreshScenarioControls={() => handleRefreshScenarioControls(activeScenario)}
              onSaveScenarioControl={handleSaveScenarioControl}
              isGeneratingActions={isGeneratingActions}
            />
          ) : (
            <Box style={{ minHeight: 560 }} />
          )}
        </Paper>
      </Box>

      {/* Notification */}
      {notification.show && (
        <Notification
          title={notification.type === 'error' ? 'Error' : 'Information'}
          color={notification.type === 'error' ? 'red' : 'blue'}
          onClose={closeNotification}
          sx={{ position: 'fixed', top: 20, right: 20, zIndex: 1000 }}
        >
          {notification.message}
        </Notification>
      )}
    </Container>
  );
}

export const SECURITY_ASSESSMENT_STEPS = ['Threat Level', 'Attack Surface Analysis', 'Asset Value'];